import { makeStyles, Theme, UIBox } from "../index";
import UITypography from "./UITypography";
import UIModalDialog from "./UIModalDialog";
import { Warning as WarningIcon } from "../icons";
import { EmailDeliveryFailure } from "../../api/types";
import {
  emailDeliveryFailureReason,
  emailDeliveryFailureType,
  formatEmailDeliveryFailureDate
} from "../../utils/emailDelivery";

export interface RemoveEmailSuppressionDialogProps {
  open: boolean;
  failure: EmailDeliveryFailure | null | undefined;
  userName?: string | null;
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const RemoveEmailSuppressionDialog = ({
  open,
  failure,
  userName,
  isLoading = false,
  onConfirm,
  onCancel,
}: RemoveEmailSuppressionDialogProps) => {
  const classes = useStyles();

  if (!failure) return null;

  return (
    <UIModalDialog
      open={open}
      onClose={onCancel}
      type="warn"
      icon={<WarningIcon />}
      title="Remove from suppression list"
      subtitle="Emails will be sent to this address again"
      maxWidth="sm"
      fullWidth
      actions={[
        {
          label: "Cancel",
          onClick: onCancel,
          variant: "text",
          disabled: isLoading,
        },
        {
          label: "Remove from suppression list",
          onClick: onConfirm,
          variant: "contained",
          color: "error",
          loading: isLoading,
        },
      ]}
    >
      <UIBox className={classes.content}>
        <UITypography variant="body1" className={classes.message}>
          The mail provider rejected an email sent to this address, or the recipient reported it as spam, so the
          address was placed on the suppression list. No further emails (including welcome emails and password resets)
          are delivered to it until it is removed from the list.
        </UITypography>

        <UIBox className={classes.userCard} data-testid="email-delivery-failure-detail">
          <UIBox className={classes.userHeader}>
            {userName && (
              <UITypography variant="subtitle1" className={classes.userName}>
                {userName}
              </UITypography>
            )}
            <UITypography variant="body2" className={classes.userEmail}>
              {failure.email}
            </UITypography>
          </UIBox>
          <UIBox className={classes.details}>
            <UITypography variant="body2" className={classes.label}>Failure type</UITypography>
            <UITypography variant="body2">{emailDeliveryFailureType(failure)}</UITypography>
            <UITypography variant="body2" className={classes.label}>Reason</UITypography>
            <UITypography variant="body2">{emailDeliveryFailureReason(failure)}</UITypography>
            <UITypography variant="body2" className={classes.label}>Failed at</UITypography>
            <UITypography variant="body2">{formatEmailDeliveryFailureDate(failure.eventAt)}</UITypography>
            {failure.diagnosticCode && (
              <>
                <UITypography variant="body2" className={classes.label}>Mail server response</UITypography>
                <UITypography variant="body2" className={classes.diagnosticCode}>{failure.diagnosticCode}</UITypography>
              </>
            )}
          </UIBox>
        </UIBox>

        <UITypography variant="body1" className={classes.message}>
          Before removing it, make sure the address is correct and the mailbox is reachable. If the address is wrong,
          update the user&apos;s email instead. Repeated delivery failures can lead to your account being suspended.
        </UITypography>
      </UIBox>
    </UIModalDialog>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
  },
  message: {
    fontSize: "1rem",
    lineHeight: 1.6,
    color: theme.palette.text.primary,
  },
  userCard: {
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  userHeader: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  userName: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  userEmail: {
    color: theme.palette.text.secondary,
    fontFamily: "monospace",
  },
  details: {
    display: "grid",
    gridTemplateColumns: "160px 1fr",
    gap: 8,
  },
  label: {
    color: theme.palette.text.secondary,
  },
  diagnosticCode: {
    fontFamily: "monospace",
    wordBreak: "break-word",
  },
}));

export default RemoveEmailSuppressionDialog;
